import {
  ActionIcon,
  Flex,
  Group,
  GroupProps,
  Text,
  Tooltip,
} from "@mantine/core"
import {
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { DottedMenu, DottedMenuProps } from "./dotted-menu"

interface Props {
  buttonGroupProps?: GroupProps
  dottedMenuProps?: DottedMenuProps
  inlineIconSize?: number
  inlineLabelWidth?: number
  inlineLabelMarginTop?: number
  showItemNames?: boolean
}

export function ResponsiveDottedMenuActions({
  buttonGroupProps,
  dottedMenuProps,
  inlineIconSize = 26,
  inlineLabelWidth = 50,
  inlineLabelMarginTop = 8,
  showItemNames = false,
}: Props) {
  const actionsRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === "undefined" ? 0 : window.innerWidth
  )

  const validDottedMenuItems = useMemo(
    () =>
      (dottedMenuProps?.items?.filter((item) => item !== null) ??
        []) as NonNullable<DottedMenuProps["items"][number]>[],
    [dottedMenuProps?.items]
  )

  const inlineEligibleItems = useMemo(() => {
    const items: NonNullable<DottedMenuProps["items"][number]>[] = []

    for (const item of validDottedMenuItems) {
      if (!item.leftSection) {
        break
      }

      items.push(item)
    }

    return items
  }, [validDottedMenuItems])

  const [inlineCount, setInlineCount] = useState(inlineEligibleItems.length)

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    setInlineCount(inlineEligibleItems.length)
  }, [viewportWidth, inlineEligibleItems.length])

  useLayoutEffect(() => {
    const element = actionsRef.current
    if (!element) {
      return
    }

    if (element.scrollWidth > element.clientWidth && inlineCount > 0) {
      setInlineCount((current) => Math.max(0, current - 1))
    }
  }, [viewportWidth, inlineCount])

  const inlineItems = useMemo(
    () => validDottedMenuItems.slice(0, inlineCount),
    [validDottedMenuItems, inlineCount]
  )

  const dottedMenuItems = useMemo(
    () => validDottedMenuItems.slice(inlineCount),
    [validDottedMenuItems, inlineCount]
  )

  const dottedMenuPropsWithRestItems = useMemo(
    () =>
      dottedMenuProps
        ? {
            ...dottedMenuProps,
            items: dottedMenuItems,
          }
        : undefined,
    [dottedMenuProps, dottedMenuItems]
  )

  return (
    <Group wrap="nowrap" style={{ minWidth: 0 }} ref={actionsRef}>
      {buttonGroupProps ? <Group {...buttonGroupProps} /> : null}
      {inlineItems.map((item, index) => {
        const label = typeof item.children === "string" ? item.children : ""
        const inlineIcon = isValidElement(item.leftSection)
          ? cloneElement(item.leftSection as React.ReactElement<any>, {
              size: inlineIconSize,
            })
          : item.leftSection

        const actionIcon = (
          <ActionIcon
            variant="subtle"
            size="xl"
            color={item.color}
            disabled={item.disabled}
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
              item.onClick?.(event)
            }}
            aria-label={label}
          >
            {inlineIcon}
          </ActionIcon>
        )

        return (
          <Flex
            key={`inline-menu-item-${index}`}
            direction="column"
            align="center"
            style={{ marginTop: inlineLabelMarginTop }}
          >
            <Tooltip label={label} withArrow>
              {actionIcon}
            </Tooltip>
            <Text
              size="xs"
              ta="center"
              w={inlineLabelWidth}
              truncate
              style={{
                visibility: showItemNames ? "visible" : "hidden",
                height: showItemNames ? "auto" : 16,
              }}
            >
              {label}
            </Text>
          </Flex>
        )
      })}
      {dottedMenuPropsWithRestItems && dottedMenuItems.length > 0 ? (
        <DottedMenu {...dottedMenuPropsWithRestItems} />
      ) : null}
    </Group>
  )
}
